import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { FC, useCallback } from 'react';
import { useBankOfferParams, useBookingFlowCity } from '@/logic/hooks';
import {
  useBooking,
  useDialog,
  useMovieDetailsState,
  useShowTimesDrawer,
  useSnack,
} from '@/logic/state';
import { useGetMovieDetails, useInitOrder } from '@/logic/services';

import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { LoadingButton } from '@mui/lab';
import { PATHS } from '@/constants/routes';
import axios from 'axios';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { CloseButton } from '@/components/shared/close-button';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { useUnipalOfferParams } from '@/hooks/use-unipal-offer-params';
import { storage } from '@/logic/libs';

const Film_HAS_BEEN_STARTED_CODE = 20;

// TODO: check all id comparison in muvi website to use type of id === 'number'
// TODO: refactor typeof conditions to reduce code
// TODO: make sure to set booking and order to null before display rules of entry dialog in offers flow

export const RulesOfEntryDialog: FC = () => {
  const theme = useTheme();
  const router = useRouter();

  const isShowTimesDrawerOpen = useShowTimesDrawer(s => s.isShowTimesDrawerOpen, shallow);
  const movie = useMovieDetailsState(s => s.movie, shallow);

  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const [setBooking, setIsLegalBookingFlow] = useBooking(
    s => [s.setBooking, s.setIsLegalBookingFlow],
    shallow
  );
  const { getBankOfferParams } = useBankOfferParams();
  const { getUnipalOfferParams } = useUnipalOfferParams();
  const { cityId } = useBookingFlowCity();

  const isFilmRateAvailable = Boolean(
    dialog?.rulesOfEntryDialogPayload?.movieId === movie?.film?.id &&
      router.pathname === PATHS.MOVIES_SLUG
  );

  const {
    data: movieDetails,
    isFetching: isMovieFetching,
    isSuccess: isMovieSuccess,
  } = useGetMovieDetails(
    {
      query: {
        id: dialog?.rulesOfEntryDialogPayload?.movieId,
      },
      params: {
        cityId,
        ...getBankOfferParams(),
        ...getUnipalOfferParams(),
      },
    },
    {
      enabled: !isFilmRateAvailable,
      onError: error => {
        if (axios.isAxiosError(error)) {
          setDialog({});
        }
      },
    }
  );

  const initOrder = useInitOrder();

  const rate = isFilmRateAvailable ? movie?.film?.rate : movieDetails?.film?.rate;

  // TODO: change main dialog width and height to be responsive
  // TODO: fix lists dots left margin

  // TODO: check the following function placement
  const handleBackwardClick = useCallback(() => {
    setDialog(prevState => ({
      wrapper: false,
      rulesOfEntryDialogPayload: prevState?.rulesOfEntryDialogPayload,
    }));
    setTimeout(() => {
      setDialog(prevState => ({
        wrapper: true,
        isRulesOfEntryDialog: true,
        rulesOfEntryDialogPayload: prevState?.rulesOfEntryDialogPayload,
      }));
    }, 500);
  }, [setDialog]);

  const openTermsAndConditionsDialog = useCallback(() => {
    setDialog(prevState => ({
      wrapper: false,
      rulesOfEntryDialogPayload: prevState?.rulesOfEntryDialogPayload,
    }));
    setTimeout(() => {
      setDialog(prevState => ({
        wrapper: true,
        isTermsAndConditionsForm: true,
        termsAndConditionsFormPayload: {
          handleBackwardClick,
        },
        rulesOfEntryDialogPayload: prevState?.rulesOfEntryDialogPayload,
      }));
    }, 500);
  }, [handleBackwardClick, setDialog]);

  const openHouseRulesDialog = useCallback(() => {
    setDialog(prevState => ({
      wrapper: false,
      rulesOfEntryDialogPayload: prevState?.rulesOfEntryDialogPayload,
    }));
    setTimeout(() => {
      setDialog(prevState => ({
        wrapper: true,
        isHouseRulesDialog: true,
        rulesOfEntryDialogPayload: prevState?.rulesOfEntryDialogPayload,
      }));
    }, 500);
  }, [setDialog]);

  const handleAgreeClick = useCallback(() => {
    initOrder.safeMutateAsync(
      {
        data: {
          sessionId: dialog?.rulesOfEntryDialogPayload?.sessionId,
          clientDate: DateTime.now().toISO(),
          ...(dialog?.rulesOfEntryDialogPayload.offerId
            ? {
                offerId: dialog?.rulesOfEntryDialogPayload.offerId,
              }
            : {}),
          ...getBankOfferParams(),
          ...getUnipalOfferParams(),
        },
      },
      {
        onSuccess: data => {
          storage.orderAnalyticsSubmit.set({ id: 0, isSubmitted: false });

          const activeExperienceTab = data?.experiences.findIndex(
            experience => experience.id === data?.order.experience.id
          );

          const activeSessions = data?.experiences.find(
            experience => experience.id === data?.order.experience.id
          ).sessions;

          const activeShowTimeTab = data?.experiences
            .find(experience => experience.id === data?.order.experience.id)
            .sessions.findIndex(session => session.id === data?.order.session.id);

          setIsLegalBookingFlow(true);
          setBooking({
            sessionId: dialog?.rulesOfEntryDialogPayload?.sessionId,
            isThreeD: dialog?.rulesOfEntryDialogPayload?.isThreeD,
            orderId: data?.order.id,
            movieId: data?.order.film.id,
            movieSlug: data?.order.film.uniqueName,
            experiences: data?.experiences,
            activeExperienceTab,
            activeSessions,
            activeShowTimeTab,
            maxSeats: data?.maxSeats,
            expireAt: data?.order.expireAt,
            paymentGateway: data?.order.paymentGateway,
            offer: data?.offer,
            cityId,
            prevOfferFlowLegalPath:
              router.pathname === PATHS.OFFERS_SLUG_SHOWS && !isShowTimesDrawerOpen
                ? router.asPath
                : router.pathname === PATHS.MOVIES_SLUG &&
                  router.query.offerId &&
                  !isShowTimesDrawerOpen
                ? `/movies/${router.query.slug}/?cityId=${cityId}`
                : null,
          });
          setDialog({});
          if (
            router.pathname === PATHS.MOVIES_SLUG_BOOKING ||
            router.pathname === PATHS.BOOKINGS_ID_CHECKOUT
          ) {
            router.replace(`/movies/${data?.order.film.uniqueName}/booking`);
          } else {
            router.push(`/movies/${data?.order.film.uniqueName}/booking`);
          }
        },
        onError: error => {
          if (axios.isAxiosError(error)) {
            if (error.response.data.code === Film_HAS_BEEN_STARTED_CODE) {
              setDialog({});
              dialog?.rulesOfEntryDialogPayload?.refetchFunction();
            }
          }
        },
      }
    );
  }, [
    cityId,
    dialog?.rulesOfEntryDialogPayload,
    getBankOfferParams,
    getUnipalOfferParams,
    initOrder,
    isShowTimesDrawerOpen,
    router,
    setBooking,
    setDialog,
    setIsLegalBookingFlow,
    setSnack,
  ]);

  const handleCloseDialog = () => {
    setDialog({});
  };

  return (
    <>
      <DialogTitle>
        <CloseButton handleClick={handleCloseDialog} />
      </DialogTitle>
      {isMovieFetching ? (
        <Stack
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          <CustomizedCircularProgress size={40} thickness={4} />
        </Stack>
      ) : isMovieSuccess || isFilmRateAvailable ? (
        <>
          <DialogContent
            sx={{
              color: theme.palette.grey[60],
            }}
          >
            {rate && (
              <>
                <Typography
                  component='div'
                  sx={{
                    textAlign: 'center',
                    marginBottom: pxToRem(24),
                    fontSize: pxToRem(22),
                    fontFamily: 'co-head-line-corp-bold',
                    color: theme.palette.common.black,
                  }}
                >
                  <FormattedMessage id='booking.rules_of_entry' />
                </Typography>
                <Stack
                  direction='row'
                  spacing={2}
                  sx={{
                    alignItems: 'center',
                    marginBottom: pxToRem(16),
                  }}
                >
                  <ImageBoxFuture
                    img={rate?.iconUrl}
                    title={rate.title}
                    objectFit='cover'
                    sx={{
                      minWidth: '54px',
                      minHeight: '54px',
                    }}
                  />

                  <Typography>{rate.description}</Typography>
                </Stack>
                <Stack component='ul' spacing={2} sx={{ ml: 2 }}>
                  {rate.rules.map(rule => (
                    <Typography key={rule.id} component='li'>
                      {rule.text}
                    </Typography>
                  ))}
                </Stack>

                <Divider
                  sx={{
                    marginY: pxToRem(24),
                    border: 'none',
                    borderTop: `${pxToRem(1)} solid ${theme.palette.grey[20]}`,
                  }}
                />
              </>
            )}
            <Stack
              sx={{
                alignItems: 'center',
                fontFamily: 'co-head-line-corp-bold',
                marginBottom: pxToRem(26),
              }}
            >
              <Typography
                sx={{
                  marginBottom: pxToRem(8),
                  color: theme.palette.grey[80],
                }}
              >
                <FormattedMessage id='booking.agree' />
              </Typography>
              <Button
                id='rules-of-entry-terms'
                type='button'
                sx={{
                  fontFamily: theme.palette.typography.font.bold,
                  marginBottom: pxToRem(3),
                  color: theme.palette.link.main,
                }}
                onClick={openTermsAndConditionsDialog}
              >
                <FormattedMessage id='terms.and.conditions' />
              </Button>
              <Button
                id='rules-of-entry-house-rules'
                type='button'
                sx={{
                  fontFamily: theme.palette.typography.font.bold,
                  color: theme.palette.link.main,
                }}
                onClick={openHouseRulesDialog}
              >
                <FormattedMessage id='booking.house_rules' />
              </Button>
            </Stack>
            {dialog?.rulesOfEntryDialogPayload?.isAfterMidnight && (
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  marginBottom: pxToRem(32),
                }}
                spacing={2}
              >
                <ImageBoxFuture
                  img='/icons/booking/midnight.svg'
                  title={rate.title}
                  objectFit='contain'
                  sx={{
                    width: '49px',
                    height: '54px',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: pxToRem(14),
                    fontFamily: 'co-head-line-corp-bold',
                    lineHeight: pxToRem(24),
                  }}
                >
                  <FormattedMessage id='booking.after_midnight' />
                </Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              id='order-cancel'
              type='button'
              fullWidth
              sx={{
                fontFamily: theme.palette.typography.font.bold,
                color: theme.palette.grey[70],
              }}
              onClick={handleCloseDialog}
            >
              <FormattedMessage id='global.cancel' />
            </Button>
            <LoadingButton
              id='order-init'
              variant='contained'
              fullWidth
              loading={initOrder.isLoading}
              onClick={handleAgreeClick}
              sx={{ height: { tablet: pxToRem(56), mobile: '56px' } }}
            >
              <FormattedMessage id='global.agree' />
            </LoadingButton>
          </DialogActions>
        </>
      ) : null}
    </>
  );
};
